/**
 * 平台角色导航配置与权限映射（纯真实业务配置，无任何 mock）
 */

export const ROLE_NAV = {
  platform_admin: ['templates', 'studio', 'materials', 'colorPalette', 'fonts', 'settings'],
  admin: ['templates', 'studio', 'materials', 'colorPalette', 'fonts', 'settings'],
  boss: ['templates', 'studio', 'materials', 'colorPalette', 'fonts', 'settings'],
  store_manager: ['templates', 'studio', 'materials', 'colorPalette', 'fonts'],
  manager: ['templates', 'studio', 'materials', 'colorPalette', 'fonts'],
  designer_leader: ['templates', 'studio', 'materials', 'fonts', 'tasks'],
  designer: ['templates', 'studio', 'materials', 'fonts', 'tasks'],
  customer_service: ['templates', 'studio', 'materials', 'fonts'],
  service: ['templates', 'studio', 'materials', 'fonts'],
};

export const ROLE_PERMS = {
  platform_admin: {
    manageMasterTpl: true,
    editStoreTpl: true,
    uploadTpl: true,
    importPdfTemplate: true,
    deleteTemplate: true,
    deleteTpl: true,
    importSource: true,
    exportPdf: true,
    editPrintConfig: true,
    manageMembers: true,
    manageMaterials: true,
    uploadMaterials: true,
    manageColorPalette: true,
    uploadFonts: true,
    deleteFonts: true,
  },
  boss: {
    manageMasterTpl: true,
    editStoreTpl: true,
    uploadTpl: true,
    importPdfTemplate: true,
    deleteTemplate: true,
    deleteTpl: true,
    importSource: true,
    exportPdf: true,
    editPrintConfig: true,
    manageMembers: true,
    manageMaterials: true,
    uploadMaterials: true,
    manageColorPalette: true,
    uploadFonts: true,
    deleteFonts: true,
  },
  store_manager: {
    manageMasterTpl: false,
    editStoreTpl: true,
    uploadTpl: true,
    importPdfTemplate: true,
    deleteTemplate: false,
    deleteTpl: false,
    importSource: true,
    exportPdf: true,
    editPrintConfig: true,
    manageMembers: true,
    manageMaterials: true,
    uploadMaterials: true,
    manageColorPalette: true,
    uploadFonts: true,
    deleteFonts: false,
  },
  designer_leader: {
    manageMasterTpl: false,
    editStoreTpl: false,
    uploadTpl: false,
    importPdfTemplate: false,
    deleteTemplate: true,
    deleteTpl: true,
    importSource: true,
    exportPdf: true,
    editPrintConfig: false,
    manageMembers: false,
    manageMaterials: true,
    uploadMaterials: true,
    manageColorPalette: false,
    uploadFonts: true,
    deleteFonts: false,
  },
  designer: {
    manageMasterTpl: false,
    editStoreTpl: false,
    uploadTpl: false,
    importPdfTemplate: false,
    deleteTemplate: false,
    deleteTpl: false,
    importSource: true,
    exportPdf: true,
    editPrintConfig: false,
    manageMembers: false,
    manageMaterials: true,
    uploadMaterials: true,
    manageColorPalette: false,
    uploadFonts: true,
    deleteFonts: false,
  },
  customer_service: {
    manageMasterTpl: false,
    editStoreTpl: false,
    uploadTpl: false,
    importPdfTemplate: false,
    deleteTemplate: false,
    deleteTpl: false,
    importSource: true,
    exportPdf: true,
    editPrintConfig: false,
    manageMembers: false,
    manageMaterials: true,
    uploadMaterials: true,
    manageColorPalette: false,
    csBranchEdit: true,
    uploadFonts: true,
    deleteFonts: false,
  },
};

export const NAV_LABELS = {
  templates: '模版库',
  templates1: '模版库',
  tasks: '我的任务',
  materials: '素材库',
  fonts: '字体库',
  studio: '设计中心',
  settings: '全局设置',
  members: '成员管理',
  colorPalette: '色板配置',
};

export const ALL_NAV_ORDER = [
  'templates',
  'studio',
  'materials',
  'colorPalette',
  'fonts',
  'settings',
  'tasks',
];

/** 临时从侧栏下线的页面。恢复「我的任务」时清空此数组即可，不必改任务页代码。 */
export const HIDDEN_NAV_VIEWS = Object.freeze(['tasks']);

export function isNavViewHidden(view) {
  return HIDDEN_NAV_VIEWS.includes(view);
}

function excludeHiddenNav(items = []) {
  return items.filter(item => !isNavViewHidden(item));
}

export const ROLE_KEY_ALIAS_MAP = {
  boss: 'boss',
  superadmin: 'platform_admin',
  admin: 'platform_admin',
  platform_admin: 'platform_admin',
  shop_keeper: 'store_manager',
  store_manager: 'store_manager',
  manager: 'store_manager',
  shop_manager: 'store_manager',
  designer_leader: 'designer_leader',
  designer: 'designer',
  salesperson: 'customer_service',
  customer_service: 'customer_service',
  service: 'customer_service',
  factory: 'customer_service',
  finance: 'customer_service',
};

/**
 * 计算多个角色（或单个角色）的导航菜单并集，按固定标准顺序排列
 */
export function resolveMergedRoleNav(roles = []) {
  const roleList = Array.isArray(roles) ? roles : [roles];
  if (!roleList.length) return excludeHiddenNav([...ROLE_NAV.designer]);

  const navSet = new Set();
  roleList.forEach((r) => {
    if (!r) return;
    const key = String(r).trim().toLowerCase().replace(/^role_/, '');
    const mappedKey = ROLE_KEY_ALIAS_MAP[key] || key;
    const items = ROLE_NAV[mappedKey] || ROLE_NAV[key] || [];
    items.forEach((item) => navSet.add(item));
  });

  if (navSet.size === 0) return excludeHiddenNav([...ROLE_NAV.designer]);

  return excludeHiddenNav(ALL_NAV_ORDER.filter((item) => navSet.has(item)));
}

/**
 * 计算多个角色（或单个角色）的权限并集（布尔 OR 运算）
 */
export function resolveMergedRolePerms(roles = []) {
  const roleList = Array.isArray(roles) ? roles : [roles];
  if (!roleList.length) return { ...ROLE_PERMS.designer };

  const merged = {};
  roleList.forEach((r) => {
    if (!r) return;
    const key = String(r).trim().toLowerCase().replace(/^role_/, '');
    const mappedKey = ROLE_KEY_ALIAS_MAP[key] || key;
    const p = ROLE_PERMS[mappedKey] || ROLE_PERMS[key] || ROLE_PERMS.designer;
    Object.keys(p).forEach((permKey) => {
      merged[permKey] = Boolean(merged[permKey] || p[permKey]);
    });
  });

  return merged;
}
